<template>
  <div class="radial-meter position-relative" :id="uniqueId" :style="`width: ${width}px; height: ${height}px;`">
    <div class="position-absolute radial-slot"/>
    <div class="d-flex justify-content-center w-100 h-100">
      <div class="align-self-end">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import uuidv4 from 'uuid/v4'
import * as d3 from 'd3'

export default {
  name: 'RadialMeter',
  props: {
    label: {
      type: String
    },
    /** The width of the generated SVG. */
    width: {
      type: Number,
      default: 200
    },
    /** Since there's a bit of overhang on the radial, we compensate for it by bumping the height up a bit. */
    heightOffset: {
      type: Number,
      default: 25
    },
    /** The width of the radial track */
    trackWidth: {
      type: Number,
      default: 12
    },
    /** The radius of the circles at the ends of the tracks */
    cornerRadius: {
      type: Number,
      default: 10
    },
    /** The duration of the animation */
    duration: {
      type: Number,
      default: 700
    },
    /** The transition function used */
    transitionFunc: {
      type: Function,
      default: d3.easeCubic
    },
    /** The color of the radial */
    color: {
      type: String,
      default: '#50CD89'
    },
    /** The underlying track color. If not specified, will default to a lightened version of `color`. */
    trackColor: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      // The amount of overhang on the arc. Having a 180 degree semi-circle arc looked kind of boring, so it's
      // extended slightly via this parameter (in radians)
      arcPadding: 0.2,
      // The container element
      container: null,
      // The element containing the background track
      radialTracks: null,
      // The element containing the arc used for animation
      radialArc: null,
      // The element containing the foreground meter
      radialMeter: null
    }
  },
  methods: {
    /**
     * Initialize the component.
     */
    init () {
      const outerRadius = (this.width / 2)
      const innerRadius = outerRadius - this.trackWidth

      const mainColor = d3.rgb(this.color)

      let trackColor
      if (this.trackColor == null) {
        trackColor = d3.rgb(this.color)
        trackColor.opacity = 0.15
      } else {
        trackColor = d3.rgb(this.trackColor)
      }

      this.container = d3.select('#' + this.uniqueId + ' .radial-slot')
        .append('svg')
        .attr('width', this.width)
        .attr('height', this.height)
        .append('g')
        .attr('transform', 'translate(' + this.width / 2 + ',' + (this.height - this.heightOffset) + ')')

      const startAngle = 0 - this.arcPadding
      const endAngle = Math.PI + this.arcPadding

      const radialTrackArc = d3.arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(this.cornerRadius)
        .startAngle(startAngle)
        .endAngle(endAngle)

      this.radialTracks = this.container.append('path')
        .attr('d', radialTrackArc)
        .attr('transform', 'rotate(-90)')
        .style('fill', trackColor)

      this.radialArc = d3.arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(this.cornerRadius)
        .startAngle(startAngle)

      this.radialMeter = this.container.append('path')
        .datum({ endAngle: startAngle })
        .attr('d', this.radialArc)
        .attr('transform', 'rotate(-90)')
        .style('fill', mainColor)
    },
    /**
     * Perform a transition to a new value.
     */
    runTransition (newValue) {
      const thisComponent = this
      this.radialMeter.transition()
        .duration(this.duration)
        .ease(this.transitionFunc)
        .attrTween('d', d => {
          // Set up a function to interpolate from the existing endAngle value to a new one (compensated with arcPadding)
          const interpolate = d3.interpolate(d.endAngle, ((Math.PI + (2 * this.arcPadding)) * newValue / 100) - this.arcPadding)
          return t => {
            d.endAngle = interpolate(t)
            return thisComponent.radialArc(d)
          }
        })
    }
  },
  mounted () {
    this.init()
  },
  computed: {
    height () {
      return this.width / 2 + this.heightOffset
    },
    /**
     * @return string
     */
    uniqueId () {
      return 'radial-' + uuidv4().toString()
    }
  }
}
</script>

<style scoped>
</style>
